// FILE: FeaturedBlogs.js

import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    IconButton,
    MobileStepper,
    Tooltip,
    Grid,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    useTheme,
} from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import DOMPurify from "dompurify";
import PersonIcon from "@mui/icons-material/Person";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import api from "../axios/axiosInstance";

const FeaturedBlogs = () => {
    const [blogs, setBlogs] = useState([]);
    const [activeStep, setActiveStep] = useState(0);
    const [modalBlog, setModalBlog] = useState(null);
    const theme = useTheme();

    useEffect(() => {
        api
            .get("/grades/blog-list/")
            .then((res) => setBlogs(res.data.slice(0, 3))) // Limit to latest 3
            .catch((err) => console.error("❌ Blog fetch failed:", err));
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveStep((prev) => (blogs.length > 0 ? (prev + 1) % blogs.length : 0));
        }, 7000);
        return () => clearInterval(timer);
    }, [blogs]);

    const handleNext = () => setActiveStep((prev) => (prev + 1) % blogs.length);
    const handleBack = () => setActiveStep((prev) => (prev - 1 + blogs.length) % blogs.length);

    const formatDate = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const truncateHtml = (html = "", wordLimit = 100) => {
        const div = document.createElement("div");
        div.innerHTML = html;
        const words = div.textContent.split(" ");
        return words.length > wordLimit
            ? words.slice(0, wordLimit).join(" ") + "..."
            : div.textContent;
    };

    if (!blogs.length) return null;
    const current = blogs[activeStep];

    return (
        <>
            <Box sx={{ mt: 8, px: { xs: 2, sm: 4 } }}>
                <Typography
                    variant="h4"
                    gutterBottom
                    fontWeight="bold"
                    color={theme.palette.primary.main}
                    sx={{ mb: 4, textAlign: "center" }}
                >
                    🌟 Featured Blog
                </Typography>

                <Grid container spacing={4} direction="column" alignItems="center">
                    <Grid item xs={12} sx={{ width: '100%' }}>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeStep}
                                initial={{ opacity: 0, y: 40 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -40 }}
                                transition={{ duration: 0.6 }}
                            >
                                <Paper
                                    elevation={6}
                                    component={motion.div}
                                    whileHover={{ scale: 1.01 }}
                                    sx={{
                                        width: "100%",
                                        maxWidth: 700,
                                        height: 220, // trimmed down height since no content
                                        overflow: "hidden",
                                        borderRadius: 3,
                                        boxShadow: 6,
                                        bgcolor: "#FBFFF5",
                                        p: 3,
                                        border: `2px solid #6E8B3D`,
                                        cursor: "pointer",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "space-between",
                                    }}
                                    onClick={() => setModalBlog(current)}
                                >
                                    <Box>
                                        <Typography
                                            variant="h5"
                                            fontWeight="bold"
                                            gutterBottom
                                            sx={{ color: "#47672A" }}
                                        >
                                            {current.topic}
                                        </Typography>

                                        {current.sub_topic && (
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontStyle: "italic", color: "#6E8B3D", fontWeight: 600 }}
                                            >
                                                {current.sub_topic}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Box
                                        mt={2}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        borderTop={`1px solid ${theme.palette.divider}`}
                                        pt={1.5}
                                    >
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <PersonIcon fontSize="small" />
                                            <Tooltip title="Author">
                                                <Typography variant="caption">{current.name}</Typography>
                                            </Tooltip>
                                        </Box>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <CalendarMonthIcon fontSize="small" />
                                            <Tooltip title="Date Posted">
                                                <Typography variant="caption">
                                                    {formatDate(current.date_created)}
                                                </Typography>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Paper>
                            </motion.div>
                        </AnimatePresence>
                    </Grid>

                    <Grid item>
                        <MobileStepper
                            variant="dots"
                            steps={blogs.length}
                            position="static"
                            activeStep={activeStep}
                            sx={{
                                background: "transparent",
                                "& .MuiMobileStepper-dot": {
                                    backgroundColor: "#DAD3C1",
                                },
                                "& .MuiMobileStepper-dotActive": {
                                    backgroundColor: "#6E8B3D",
                                },
                            }}
                            nextButton={
                                <IconButton size="small" onClick={handleNext} sx={{ color: "#6E8B3D" }}>
                                    <KeyboardArrowRight />
                                </IconButton>
                            }
                            backButton={
                                <IconButton size="small" onClick={handleBack} sx={{ color: "#6E8B3D" }}>
                                    <KeyboardArrowLeft />
                                </IconButton>
                            }
                        />
                    </Grid>
                </Grid>
            </Box>

            {/* Blog Modal */}
            <Dialog open={!!modalBlog} onClose={() => setModalBlog(null)} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        bgcolor: "#6E8B3D",
                        color: "white",
                        fontWeight: "bold",
                        py: 2,
                        px: 3,
                        fontSize: "1.4rem",
                    }}
                >
                    {modalBlog?.topic}
                </DialogTitle>
                <DialogContent dividers sx={{ backgroundColor: "#FAFAF5", py: 3, px: 4 }}>
                    {modalBlog?.sub_topic && (
                        <Typography
                            variant="subtitle2"
                            sx={{ fontStyle: "italic", mb: 2, color: "#56643F" }}
                        >
                            {modalBlog.sub_topic}
                        </Typography>
                    )}
                    <Typography
                        variant="body1"
                        component="div"
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(modalBlog?.blog_details || ""),
                        }}
                        sx={{ color: "#3F3F3F", lineHeight: 1.7 }}
                    />
                </DialogContent>
                <DialogActions sx={{ justifyContent: "space-between", px: 3, py: 2 }}>
                    <Typography variant="caption" sx={{ color: "#777" }}>
                        Author: {modalBlog?.name} — {formatDate(modalBlog?.date_created)}
                    </Typography>
                    <Button onClick={() => setModalBlog(null)} variant="outlined" color="success">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default FeaturedBlogs;
